import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import { BR_LOCALE } from '@/constants/locale.js'

export const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.radiohabblet.com.br'
export const SITE_NAME = 'Rádio Habblet'
const DEFAULT_DESCRIPTION =
  'Fã-site oficial do Habblet Hotel. Notícias, rádio online, eventos e entretenimento para a comunidade Habblet BR/PT.'
const DEFAULT_OG_IMAGE =
  'https://ppanel.radiohabblet.com.br/imagem/uploads/upload-8305286fbc449bb3f64ce6308d8f32d9.jpeg'

function stripHtml(html) {
  if (!html) return ''
  return String(html)
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function truncate(text, max = 160) {
  const clean = stripHtml(text)
  if (clean.length <= max) return clean
  return clean.slice(0, max - 1).trimEnd() + '…'
}

export function absoluteUrl(path = '/') {
  if (!path) return SITE_URL
  if (path.startsWith('http')) return path
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`
}

export function useSeo({
  title,
  description,
  image,
  type = 'website',
  path,
  noindex = false,
  publishedTime,
  modifiedTime,
  author,
  jsonLd,
} = {}) {
  const route = useRoute()
  applySeoHead(null, {
    title,
    description,
    image,
    type,
    path: path ?? route.fullPath,
    noindex,
    publishedTime,
    modifiedTime,
    author,
    jsonLd,
  }, { useHeadFn: useHead })
}

export function applySeoHead(head, options = {}, { useHeadFn } = {}) {
  const pageUrl = absoluteUrl(options.path ?? '/')
  const pageTitle = options.title
    ? `${options.title} • ${SITE_NAME}`
    : `${SITE_NAME} • Um novo jeito de fazer fã-site!`
  const pageDescription = options.description || DEFAULT_DESCRIPTION
  const pageImage = options.image || DEFAULT_OG_IMAGE

  const meta = [
    { name: 'description', content: pageDescription },
    { name: 'keywords', content: 'Habblet, fã-site, rádio, Habblet BR, Habblet PT, notícias, eventos' },
    { name: 'author', content: options.author || 'Rádio Habblet' },
    {
      name: 'robots',
      content: options.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large',
    },
    { property: 'og:site_name', content: SITE_NAME },
    { property: 'og:locale', content: 'pt_BR' },
    { property: 'og:url', content: pageUrl },
    { property: 'og:type', content: options.type || 'website' },
    { property: 'og:title', content: pageTitle },
    { property: 'og:description', content: pageDescription },
    { property: 'og:image', content: pageImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@RadioHabblet' },
    { name: 'twitter:title', content: pageTitle },
    { name: 'twitter:description', content: pageDescription },
    { name: 'twitter:image', content: pageImage },
    { name: 'citation_title', content: pageTitle },
    { name: 'citation_public_url', content: pageUrl },
    { name: 'citation_publisher', content: SITE_NAME },
  ]

  if (options.publishedTime) {
    meta.push({ property: 'article:published_time', content: options.publishedTime })
    meta.push({ name: 'citation_publication_date', content: options.publishedTime })
  }
  if (options.modifiedTime) {
    meta.push({ property: 'article:modified_time', content: options.modifiedTime })
  }
  if (options.author) {
    meta.push({ property: 'article:author', content: options.author })
    meta.push({ name: 'citation_author', content: options.author })
  }

  const scripts = []
  const jsonLdItems = Array.isArray(options.jsonLd)
    ? options.jsonLd
    : options.jsonLd
      ? [options.jsonLd]
      : []

  for (let i = 0; i < jsonLdItems.length; i++) {
    scripts.push({
      type: 'application/ld+json',
      key: `json-ld-${i}`,
      innerHTML: JSON.stringify(jsonLdItems[i]),
    })
  }

  const payload = {
    htmlAttrs: { lang: BR_LOCALE },
    title: pageTitle,
    meta: [
      { 'http-equiv': 'content-language', content: BR_LOCALE },
      ...meta,
    ],
    link: [
      { rel: 'canonical', href: pageUrl },
      { rel: 'alternate', hreflang: BR_LOCALE, href: pageUrl },
    ],
    script: scripts,
  }

  if (useHeadFn) {
    useHeadFn(payload)
    return
  }

  if (head?.push) {
    head.push(payload)
  }
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_OG_IMAGE,
    sameAs: ['https://twitter.com/RadioHabblet'],
    description: DEFAULT_DESCRIPTION,
  }
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: BR_LOCALE,
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  }
}

export function buildNewsArticleSchema({ title, description, image, url, author, publishedTime }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: title,
    description: truncate(description, 200),
    image: image ? [image] : [DEFAULT_OG_IMAGE],
    url,
    mainEntityOfPage: url,
    datePublished: publishedTime,
    author: {
      '@type': 'Person',
      name: author || SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_OG_IMAGE,
      },
    },
    isAccessibleForFree: true,
    copyrightHolder: {
      '@type': 'Organization',
      name: SITE_NAME,
    },
  }
}

export function buildArticleSchema({ title, description, image, url, author, publishedTime, type = 'Article' }) {
  return {
    '@context': 'https://schema.org',
    '@type': type,
    headline: title,
    description: truncate(description, 200),
    image: image ? [image] : [DEFAULT_OG_IMAGE],
    url,
    datePublished: publishedTime,
    author: {
      '@type': 'Person',
      name: author || SITE_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
    },
  }
}
